import type { SVGProps } from 'react'
import { useSiteBrand } from '../i18n'
import { useBrandMark, useMarkElement } from './pack'

export type IconName =
  | 'ic-sec-maps' | 'ic-sec-models' | 'ic-sec-buoys' | 'ic-sec-imagery' | 'ic-sec-satellites' | 'ic-sec-rules' | 'ic-sec-navwarn' | 'ic-sec-quotas' | 'ic-sec-prices' | 'ic-sec-reports' | 'ic-sec-settings'
  | 'ic-sst' | 'ic-salinity' | 'ic-oxygen' | 'ic-chlorophyll' | 'ic-zooplankton'
  | 'ic-currents' | 'ic-ssha' | 'ic-wind' | 'ic-bathymetry'
  | 'ic-ice' | 'ic-fog' | 'ic-clouds' | 'ic-earthquakes' | 'ic-moon'
  | 'ic-hsi' | 'ic-upwelling' | 'ic-forecast'
  | 'ic-depth' | 'ic-point' | 'ic-load' | 'ic-save-view' | 'ic-layers'
  | 'ic-legend' | 'ic-panel' | 'ic-org' | 'ic-profile' | 'ic-logout'
  | 'ic-calendar' | 'ic-search' | 'ic-sun' | 'ic-globe'

interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'name'> {
  name: IconName
}

/** Моноцветная иконка интерфейса из инлайн-спрайта. Берёт цвет через currentColor,
 *  размер — через className (по умолчанию 20px). Без свечения. */
export function Icon({ name, className, ...rest }: IconProps) {
  return (
    <svg className={className ?? 'h-5 w-5'} aria-hidden focusable={false} {...rest}>
      <use href={`#${name}`} />
    </svg>
  )
}

/**
 * Знак домена: из бренд-пака, а пока он не доехал — встроенный.
 *
 * Знак вклеивается разметкой, а не подключается картинкой: `currentColor` во
 * внешнем изображении разрешался бы против чужого документа и дал бы чёрный знак
 * на тёмной теме (подробности и защита вставки — `pack.ts`, `svgPolicy.ts`).
 *
 * Немой намеренно: имя бренда стоит рядом настоящим текстом, и подпись внутри
 * знака озвучивалась бы дублем.
 */
export function Mark({ className }: { className?: string }) {
  const brand = useSiteBrand()
  const markup = useBrandMark(brand?.logo.url)
  const size = className ?? 'h-6 w-6'
  const { svg, hostRef } = useMarkElement(markup, brand?.logo.mono ?? true)
  // Знак пака не собрался (файл не тот, разметка битая) — рисуем встроенный:
  // пустая коробка на месте эмблемы выглядела бы поломкой кабинета.
  if (!svg) return <BaseMark className={size} />
  return <span ref={hostRef} className={`inline-flex shrink-0 ${size}`} aria-hidden />
}

/**
 * Встроенный знак «Лоция» — футшток: градуированная рейка, стоящая в воде.
 * Рейка и деления берут цвет текста (`currentColor`), подводная часть и линия
 * уровня — акцент темы: синяя длина и есть отсчёт.
 *
 * Рисунок ОБЯЗАН совпадать с файлом пака (`branding/forecast/prognoz/logo.svg`): витрина
 * рисует то один знак, то другой (пак ещё не доехал / не разобрался), и разница
 * между ними была бы подменой знака при каждой загрузке кабинета. Знак менялся
 * дважды: 28.08.2026 здесь стояла компасная роза «Румба» (знак ЧУЖОГО продукта
 * после разделения брендов), 29.08.2026 — изобаты, забракованные фокус-группой
 * вместе с пурпурной гаммой.
 *
 * Цвет записан переменной темы прямо в атрибуте — той же строкой, что в файле
 * пака: класса Tailwind в самостоятельном SVG не существует, и два разных
 * способа задать один цвет разъехались бы при первой же правке палитры. Фолбэк
 * — светлый акцент «Лоции»; нужен он только внешнему изображению (там
 * переменных нет). Совпадение стережёт `mark.test.ts`.
 */
function BaseMark({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 96 96" className={className ?? 'h-6 w-6'} aria-hidden focusable={false}>
      <path
        d="M56 8V62"
        stroke="currentColor"
        strokeWidth="11"
        strokeLinecap="round"
      />
      <path
        d="M56 62V88"
        stroke="rgb(var(--accent, 3 112 190))"
        strokeWidth="11"
        strokeLinecap="round"
      />
      <path
        d="M32 20H48"
        stroke="currentColor"
        strokeWidth="7"
        strokeLinecap="round"
      />
      <path
        d="M32 34H48"
        stroke="currentColor"
        strokeWidth="7"
        strokeLinecap="round"
      />
      <path
        d="M32 48H48"
        stroke="currentColor"
        strokeWidth="7"
        strokeLinecap="round"
      />
      <path
        d="M8 62H84"
        stroke="rgb(var(--accent, 3 112 190))"
        strokeWidth="10"
        strokeLinecap="round"
      />
    </svg>
  )
}
